import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';

interface AutocompleteFieldProps<T> {
  label: string;
  placeholder: string;
  value: T | null;
  options: T[];
  loading: boolean;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  displayField: keyof T;
  onInputChange: (value: string) => void;
  onChange: (value: T | null) => void;
  minSearchLength?: number;
}

function AutocompleteField<T>({
  label,
  placeholder,
  value,
  options,
  loading,
  error = false,
  disabled = false,
  required = false,
  displayField,
  onInputChange,
  onChange,
  minSearchLength = 3
}: AutocompleteFieldProps<T>) {
  const [inputValue, setInputValue] = useState('');
  const debounceRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (value) {
      setInputValue(String(value[displayField]));
    } else {
      setInputValue('');
    }
  }, [value, displayField]);

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    
    // Debounce para evitar múltiples llamadas API
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      if (newInputValue.length >= minSearchLength) {
        onInputChange(newInputValue);
      }
    }, 300);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: T | null) => {
    onChange(newValue);
  };

  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={(option) => String(option[displayField])}
      loading={loading}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Typography variant="body2">
            {String(option[displayField])}
          </Typography>
        </Box>
      )}
      noOptionsText={
        inputValue.length < minSearchLength 
          ? `Escriba al menos ${minSearchLength} caracteres`
          : "No se encontraron resultados"
      }
    />
  );
}

export default AutocompleteField;
